/**
 * React Query hooks for template CRUD operations.
 * Provides type-safe data fetching and mutations for custom analysis templates.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  forkTemplate,
  duplicateTemplate,
} from '@/services/api';
import type {
  CreateTemplateRequest,
  UpdateTemplateRequest,
  ForkTemplateRequest,
} from '@/types';

const TEMPLATES_KEY = 'templates';

/**
 * Fetch all templates for a team (includes starter template metadata)
 */
export function useTemplates(teamId: string | undefined) {
  return useQuery({
    queryKey: [TEMPLATES_KEY, teamId],
    queryFn: () => getTemplates(teamId!),
    enabled: !!teamId,
  });
}

/**
 * Fetch a single template by ID
 */
export function useTemplate(teamId: string | undefined, templateId: string | undefined) {
  return useQuery({
    queryKey: [TEMPLATES_KEY, teamId, templateId],
    queryFn: () => getTemplate(teamId!, templateId!),
    enabled: !!teamId && !!templateId,
  });
}

/**
 * Create a new template with custom gates
 */
export function useCreateTemplate(teamId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTemplateRequest) => createTemplate(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, teamId] });
    },
  });
}

/**
 * Update an existing template
 */
export function useUpdateTemplate(teamId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: UpdateTemplateRequest }) =>
      updateTemplate(teamId!, templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, teamId] });
    },
  });
}

/**
 * Delete a template (reverts assigned projects to default)
 */
export function useDeleteTemplate(teamId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => deleteTemplate(teamId!, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, teamId] });
    },
  });
}

/**
 * Fork a starter or existing template into a new team template
 */
export function useForkTemplate(teamId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ForkTemplateRequest) => forkTemplate(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, teamId] });
    },
  });
}

/**
 * Duplicate an existing team template
 */
export function useDuplicateTemplate(teamId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => duplicateTemplate(teamId!, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, teamId] });
    },
  });
}
